import React from "react";
import { sendPasswordResetEmail } from "firebase/auth";
import { schoolApi } from "./services/api";
import { auth } from "./config/firebase";
import { teachersData, staffData, examsData, timetableData, homeworkData, admissionEnquiriesData } from "./extendedMock";

// Additional Modules & Dashboards for EduNexa 360 Phase 1

function LegacyTeachersModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  const [teachers, setTeachers] = React.useState(() => (isBlankWorkspace ? [] : teachersData));
  const [search, setSearch] = React.useState("");
  const [showAdd, setShowAdd] = React.useState(false);
  const [selectedTeacher, setSelectedTeacher] = React.useState(null);

  const filtered = teachers.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()) || t.department.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 flex">
      <main className="flex-1 p-6 sm:p-10 max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center mb-8">
          <div>
            <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 hover:underline mb-2 block">
              ← Back to Dashboard
            </button>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Teacher Management (Tenant Scoped)</h1>
            <p className="text-slate-400 text-sm">Faculty profiles, department allocation, and class teacher assignments.</p>
          </div>
          <button onClick={() => setShowAdd(true)} className="w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-6 py-3 rounded-xl text-sm shadow-lg shadow-amber-500/20" data-testid="add-teacher-btn">
            + Register New Teacher
          </button>
        </div>

        <div className="bg-slate-900 border border-slate-800 p-4 rounded-2xl mb-8">
          <input type="text" placeholder="Search teacher by name or department..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full bg-transparent text-sm text-white focus:outline-none" data-testid="teacher-search-input" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {filtered.map((tch) => (
            <div key={tch.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <img src={tch.photograph} alt="" className="w-14 h-14 rounded-2xl object-cover border border-amber-500/50" />
                  <div>
                    <h3 className="font-bold text-white text-base">{tch.name}</h3>
                    <div className="text-xs text-amber-400">{tch.designation}</div>
                    <div className="text-[11px] text-slate-400">{tch.department}</div>
                  </div>
                </div>
                <div className="space-y-2 text-xs text-slate-300 border-t border-slate-800 pt-4">
                  <div>📞 {tch.phone}</div>
                  <div>
                    🏛️ Class Teacher: <span className="font-bold text-white">{tch.classTeacherOf}</span>
                  </div>
                  <div>📚 Subjects: {tch.subjectsAssigned.join(", ")}</div>
                </div>
              </div>
              <button onClick={() => setSelectedTeacher(tch)} className="mt-6 w-full bg-slate-800 hover:bg-slate-700 text-white py-2.5 rounded-xl text-xs font-bold transition" data-testid={`view-teacher-${tch.employeeId}`}>
                View Profile & Payroll →
              </button>
            </div>
          ))}
        </div>

        {selectedTeacher && (
          <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-xl w-full p-4 sm:p-8 relative">
              <button onClick={() => setSelectedTeacher(null)} className="absolute top-6 right-6 text-slate-400 hover:text-white">
                ✕
              </button>
              <div className="flex items-center gap-4 mb-6">
                <img src={selectedTeacher.photograph} alt="" className="w-16 h-16 rounded-2xl object-cover" />
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedTeacher.name}</h2>
                  <div className="text-xs text-amber-400">{selectedTeacher.qualification}</div>
                  <div className="text-xs text-slate-400">Experience: {selectedTeacher.experience}</div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm mb-6">
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                  <span className="text-xs text-slate-400 block">Monthly Salary</span>
                  <span className="font-bold text-amber-400 text-base">₹{selectedTeacher.salary.toLocaleString()}</span>
                </div>
                <div className="bg-slate-950 p-4 rounded-xl border border-slate-800">
                  <span className="text-xs text-slate-400 block">Joining Date</span>
                  <span className="font-bold text-white text-base">{selectedTeacher.joiningDate}</span>
                </div>
              </div>
              <button onClick={() => setSelectedTeacher(null)} className="w-full bg-amber-500 text-slate-950 font-bold py-3 rounded-xl text-sm">
                Close Profile
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function LegacyStaffModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-6">Staff Management (Administrative & Support)</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {(isBlankWorkspace ? [] : staffData).map((stf) => (
            <div key={stf.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl flex items-center gap-6">
              <img src={stf.photograph} alt="" className="w-16 h-16 rounded-2xl object-cover" />
              <div>
                <h3 className="font-bold text-white text-lg">{stf.name}</h3>
                <div className="text-xs text-indigo-400">
                  {stf.designation} ({stf.department})
                </div>
                <div className="text-xs text-slate-400 mt-2">
                  📞 {stf.phone} • Salary: ₹{stf.salary.toLocaleString()}
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

function EmployeeDirectoryUI({ title, role, schoolSlug, backRoute, navigate, items, selected, setSelected, form, setForm, mode, setMode, search, setSearch, notice, setNotice, busy, uploads, setUploads, fields, classOptions = [], save, remove, upload, load }) {
  const shown = items.filter((x) => `${x.name} ${x.employee_code} ${x.department} ${x.designation}`.toLowerCase().includes(search.toLowerCase()));
  const avatar = (item) => item.photograph || `https://ui-avatars.com/api/?background=4f46e5&color=fff&name=${encodeURIComponent(item.name)}`;
  const openAdd = () => {
    setForm({
      name: "",
      email: "",
      password: "",
      employee_code: "",
      designation: "",
      department: "",
      phone: "",
      address: "",
      qualification: "",
      experience: "",
      joining_date: "",
      salary: "",
      subjects_assigned: "",
      class_teacher_of: "",
    });
    setUploads({ profile: null, face: null, document: null });
    setMode("add");
  };
  const openEdit = () => {
    setForm({ ...selected, password: "", photograph: undefined });
    setUploads({ profile: null, face: null, document: null });
    setMode("edit");
  };
  const fileField = (key, label, help, accept) => (
    <label className="rounded-xl border border-slate-700 bg-slate-950 p-4 text-xs font-semibold text-slate-300">
      {label}
      <input type="file" accept={accept} onChange={(e) => setUploads({ ...uploads, [key]: e.target.files?.[0] || null })} className="mt-3 block w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-600 file:px-3 file:py-2 file:font-bold file:text-white" />
      <span className="mt-2 block font-normal text-slate-500">{uploads[key]?.name || help}</span>
    </label>
  );
  const existingFiles = mode === "edit" && (
    <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div className="rounded-xl border border-slate-700 bg-slate-950 p-4">
        <p className="text-xs font-semibold text-slate-300">Current profile photo</p>
        {selected?.photograph ? (
          <div className="mt-3 flex items-center gap-3">
            <img src={selected.photograph} alt="Current profile" className="h-14 w-14 rounded-xl object-cover" />
            <div><p className="text-xs font-semibold text-emerald-300">Uploaded</p><button type="button" onClick={deleteProfilePhoto} className="mt-1 text-xs font-semibold text-red-300 underline">Delete photo</button></div>
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">Not uploaded</p>
        )}
      </div>
      <div className="rounded-xl border border-slate-700 bg-slate-950 p-4">
        <p className="text-xs font-semibold text-slate-300">Face attendance</p>
        <p className={`mt-3 text-xs font-semibold ${selected?.face_registered ? "text-emerald-300" : "text-amber-300"}`}>{selected?.face_registered ? "Registered and ready" : "Not registered"}</p>
      </div>
      <div className="rounded-xl border border-slate-700 bg-slate-950 p-4">
        <p className="text-xs font-semibold text-slate-300">Existing documents</p>
        {selected?.documents?.length ? (
          <div className="mt-2 flex flex-col gap-1">
            {selected.documents.map((doc) => <div key={doc.id} className="flex items-center justify-between gap-2"><a href={doc.url} target="_blank" rel="noreferrer" className="truncate text-xs text-indigo-300 underline">{doc.label || doc.name}</a><button type="button" onClick={() => deleteDocument(doc.id)} className="shrink-0 text-xs font-semibold text-red-300">Delete</button></div>)}
          </div>
        ) : (
          <p className="mt-3 text-xs text-slate-500">None uploaded</p>
        )}
      </div>
    </div>
  );
  const resetPassword = async () => {
    try {
      await sendPasswordResetEmail(auth, form.email);
      window.alert(`Password reset email sent to ${form.email}.`);
    } catch (error) {
      window.alert(error.message || "Unable to send password reset email.");
    }
  };
  const useExistingFace = async (documentId) => {
    try {
      setNotice("");
      const result = await schoolApi.registerEmployeeFaceFromDocument(schoolSlug, role, selected.id, documentId);
      setSelected(result);
      setNotice("Existing image verified and registered for face attendance.");
      await load();
    } catch (error) {
      setNotice(error.message || "Unable to register this image for face attendance.");
    }
  };
  const deleteProfilePhoto = async () => {
    if (!window.confirm("Delete this profile photo?")) return;
    try {
      setNotice("");
      const result = await schoolApi.deleteEmployeePhoto(schoolSlug, role, selected.id);
      setSelected(result); setNotice("Profile photo deleted."); await load();
    } catch (error) { setNotice(error.message || "Unable to delete the profile photo."); }
  };
  const deleteDocument = async (documentId) => {
    if (!window.confirm("Delete this uploaded document? This cannot be undone.")) return;
    try {
      setNotice("");
      const result = await schoolApi.deleteEmployeeDocument(schoolSlug, role, selected.id, documentId);
      setSelected(result); setNotice("Document deleted."); await load();
    } catch (error) { setNotice(error.message || "Unable to delete the document."); }
  };
  if (mode)
    return (
      <main className="min-h-screen bg-[#0A1128] p-4 text-slate-100 sm:p-8">
        <form onSubmit={save} className="mx-auto max-w-5xl">
          <button type="button" onClick={() => setMode("")} className="mb-5 text-sm font-semibold text-amber-400">
            ← Back to {title}
          </button>
          <div className="flex flex-col gap-2 border-b border-slate-700 pb-6 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-400">People & Administration</p>
              <h1 className="mt-1 text-3xl font-black">{mode === "edit" ? `Edit ${title.slice(0, -1)}` : `Add ${title.slice(0, -1)}`}</h1>
              <p className="mt-2 text-sm text-slate-400">Update details and upload files directly. Image URLs are not required.</p>
            </div>
            <button disabled={busy} className="rounded-xl bg-amber-500 px-6 py-3 font-bold text-slate-950 disabled:opacity-50">
              {busy ? "Saving and uploading…" : "Save details & uploads"}
            </button>
          </div>
          {notice && (
            <div role="alert" className="mt-5 rounded-xl border border-red-800 bg-red-950/60 p-4 text-sm text-red-200">
              {notice}
            </div>
          )}
          {mode === "edit" && (
            <div className="mt-5 flex flex-col gap-3 rounded-xl border border-indigo-800 bg-indigo-950/30 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-indigo-100">The existing password is securely protected and cannot be displayed. Send a reset link only when it needs changing.</p>
              <button type="button" onClick={resetPassword} className="shrink-0 rounded-lg border border-indigo-500 px-4 py-2 text-sm font-bold">
                Send password reset
              </button>
            </div>
          )}
          <section className="py-7">
            <h2 className="mb-5 text-lg font-bold">Personal and work details</h2>
            <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
              {fields
                .filter(([name]) => mode === "add" || name !== "password")
                .map(([name, label, type]) => name === "class_teacher_of" && role === "teacher" ? (
                  <label key={name} className="text-xs font-semibold text-slate-300">Class teacher of (class and section) *<select name={name} required value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm"><option value="">Select class and section</option>{classOptions.map(option => <option key={option} value={option}>{option}</option>)}</select><span className="mt-1 block font-normal text-slate-500">Only this class and section will appear on the teacher attendance page.</span></label>
                ) : (
                  <label key={name} className="text-xs font-semibold text-slate-300">
                    {label}
                    <input name={name} type={type || "text"} required={mode === "add" && ["name", "email", "password", "employee_code"].includes(name)} value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm" />
                  </label>
                ))}
            </div>
          </section>
          <section className="border-t border-slate-700 py-7">
            <h2 className="text-lg font-bold">Photos and documents</h2>
            <p className="mt-1 text-sm text-slate-400">Existing files remain saved. Choose a file only when adding or replacing one.</p>
            {existingFiles}
            <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {fileField("profile", selected?.photograph ? "Replace profile photo (optional)" : "Profile photo", selected?.photograph ? "Leave empty to keep the current photo." : "JPEG, PNG or WebP image", "image/jpeg,image/png,image/webp")}
              {fileField("face", selected?.face_registered ? "Replace face-attendance photo (optional)" : "Face-attendance photo", selected?.face_registered ? "Leave empty to keep the registered face." : "Exactly one clear, front-facing face.", "image/jpeg,image/png,image/webp")}
              {fileField("document", "Add another document (optional)", "Existing documents remain saved.", "application/pdf,image/*")}
            </div>
          </section>
          <div className="flex flex-col-reverse gap-3 border-t border-slate-700 py-6 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => setMode("")} className="rounded-xl border border-slate-700 px-6 py-3 font-bold">
              Cancel
            </button>
            <button disabled={busy} className="rounded-xl bg-amber-500 px-8 py-3 font-bold text-slate-950 disabled:opacity-50">
              {busy ? "Saving and uploading…" : "Save details & uploads"}
            </button>
          </div>
        </form>
      </main>
    );
  return (
    <main className="min-h-screen bg-[#0A1128] p-6 text-slate-100 sm:p-10">
      <div className="mx-auto max-w-7xl">
        <button onClick={() => navigate(backRoute, schoolSlug)} className="mb-3 text-xs text-amber-400">
          ← Back to dashboard
        </button>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
          <div>
            <h1 className="text-3xl font-black">{title}</h1>
            <p className="text-sm text-slate-400">Profiles, work information, uploaded files and login credentials.</p>
          </div>
          <div className="flex gap-3">
            <label className="cursor-pointer rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-bold">
              Import Excel / CSV
              <input type="file" className="hidden" accept=".xlsx,.csv" onChange={upload} />
            </label>
            <button onClick={openAdd} className="rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950">
              + Add {title.slice(0, -1)}
            </button>
          </div>
        </div>
        {notice && <p className="mb-4 rounded-xl bg-indigo-950 p-3 text-sm text-indigo-200">{notice}</p>}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`} className="mb-5 w-full rounded-xl border border-slate-700 bg-slate-900 p-3" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((item) => (
            <button key={item.id} onClick={() => setSelected(item)} className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left hover:border-indigo-500">
              <div className="flex gap-3">
                <img src={avatar(item)} alt="" className="h-14 w-14 rounded-xl object-cover" />
                <div>
                  <h2 className="font-bold">{item.name}</h2>
                  <p className="text-xs text-amber-400">{item.designation || role}</p>
                  <p className="text-xs text-slate-400">
                    {item.department || "—"} · {item.employee_code}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400">
                {item.email} · {item.phone || "No phone"}
              </p>
            </button>
          ))}
          {!shown.length && <p className="text-slate-500">No {title.toLowerCase()} found.</p>}
        </div>
      </div>
      {selected && !mode && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 p-4">
          <div className="mx-auto my-8 max-w-2xl rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <div className="flex justify-between">
              <div className="flex gap-4">
                <div><img src={avatar(selected)} alt="" className="h-20 w-20 rounded-2xl object-cover" />{selected.photograph && <button type="button" onClick={deleteProfilePhoto} className="mt-2 block text-xs font-semibold text-red-300 underline">Delete photo</button>}</div>
                <div>
                  <h2 className="text-2xl font-black">{selected.name}</h2>
                  <p className="text-amber-400">
                    {selected.designation} · {selected.employee_code}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                ["Email", selected.email],
                ["Phone", selected.phone],
                ["Department", selected.department],
                ["Joining date", selected.joining_date],
                ["Salary", selected.salary && `₹${Number(selected.salary).toLocaleString()}`],
                ["Qualification", selected.qualification],
                ["Experience", selected.experience],
                ["Subjects", selected.subjects_assigned],
                ["Class teacher", selected.class_teacher_of],
                ["Address", selected.address],
              ].map(([label, value]) => (
                <div key={label} className="rounded-xl bg-slate-950 p-3">
                  <small className="text-slate-500">{label}</small>
                  <p className="text-sm font-semibold">{value || "—"}</p>
                </div>
              ))}
            </div>
            <section className="mt-5 rounded-xl border border-slate-800 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-bold">Uploaded photos and documents</h3>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${selected.face_registered ? "bg-emerald-950 text-emerald-300" : "bg-amber-950 text-amber-300"}`}>{selected.face_registered ? "Face attendance registered" : "Face attendance not registered"}</span>
              </div>
              {selected.documents?.length ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selected.documents.map((doc) => (
                    <span key={doc.id} className="flex items-center gap-2">
                      <a href={doc.url} target="_blank" rel="noreferrer" className={`rounded-lg border px-3 py-2 text-xs ${/face attendance/i.test(doc.label || "") ? "border-emerald-700 text-emerald-300" : "border-indigo-700 text-indigo-200"}`}>
                        {doc.label || doc.name}
                      </a>
                      {!selected.face_registered && /\.(jpe?g|png|webp)$/i.test(doc.name || "") && (
                        <button type="button" onClick={() => useExistingFace(doc.id)} className="rounded-lg bg-emerald-700 px-3 py-2 text-xs font-semibold text-white">
                          Use for face attendance
                        </button>
                      )}
                      <button type="button" onClick={() => deleteDocument(doc.id)} className="rounded-lg border border-red-800 px-3 py-2 text-xs font-semibold text-red-300">Delete</button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500">No documents uploaded.</p>
              )}
            </section>
            <div className="mt-6 flex gap-3">
              <button onClick={openEdit} className="rounded-xl bg-amber-500 px-4 py-3 font-bold text-slate-950">
                Edit details & uploads
              </button>
              <button onClick={remove} className="rounded-xl bg-red-600 px-4 py-3 font-bold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {mode && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 p-4">
          <form onSubmit={save} className="mx-auto my-6 max-w-3xl rounded-3xl border border-slate-700 bg-slate-900 p-6">
            <div className="mb-5 flex justify-between">
              <div>
                <h2 className="text-xl font-black">{mode === "edit" ? `Edit ${title.slice(0, -1)}` : `Add ${title.slice(0, -1)}`}</h2>
                <p className="mt-1 text-xs text-slate-400">Upload files directly—no image URL is required.</p>
              </div>
              <button type="button" onClick={() => setMode("")}>
                ✕
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map(([name, label, type]) => (
                <label key={name} className="text-xs font-semibold text-slate-300">
                  {label}
                  <input name={name} type={type || "text"} required={mode === "add" && ["name", "email", "password", "employee_code"].includes(name)} value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm" />
                </label>
              ))}
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {fileField("profile", "Profile photo", "JPEG, PNG or WebP image", "image/jpeg,image/png,image/webp")}
              {fileField("face", "Face-attendance photo", "Exactly one clear, front-facing face. This is securely verified and encoded.", "image/jpeg,image/png,image/webp")}
              {fileField("document", "Employee document", "PDF or image document", "application/pdf,image/*")}
            </div>
            <button disabled={busy} className="mt-6 w-full rounded-xl bg-amber-500 py-3 font-bold text-slate-950 disabled:opacity-50">
              {busy ? "Saving and uploading…" : "Save details & uploads"}
            </button>
          </form>
        </div>
      )}
    </main>
  );
}

export function EmployeeModule({ schoolSlug, navigate, role, backRoute = "school-admin" }) {
  const title = role === "teacher" ? "Teachers" : role === "staff" ? "Staff" : "Accountants";
  const blank = {
    name: "",
    email: "",
    password: "",
    employee_code: "",
    designation: "",
    department: "",
    phone: "",
    address: "",
    photograph: "",
    qualification: "",
    experience: "",
    joining_date: "",
    salary: "",
    subjects_assigned: "",
    class_teacher_of: "",
  };
  const [items, setItems] = React.useState([]),
    [selected, setSelected] = React.useState(null),
    [form, setForm] = React.useState(blank),
    [mode, setMode] = React.useState(""),
    [search, setSearch] = React.useState(""),
    [notice, setNotice] = React.useState(""),
    [busy, setBusy] = React.useState(false),
    [uploads, setUploads] = React.useState({
      profile: null,
      face: null,
      document: null,
    }),
    [classOptions, setClassOptions] = React.useState([]);
  const load = () =>
    schoolApi
      .employees(schoolSlug, role)
      .then(setItems)
      .catch((e) => setNotice(e.message));
  React.useEffect(() => {
    load();
  }, [schoolSlug, role]);
  React.useEffect(() => {
    if (role !== "teacher") return;
    schoolApi.students(schoolSlug).then(rows => setClassOptions([...new Set((Array.isArray(rows) ? rows : []).map(row => `${row.class_name} · ${row.section}`))])).catch(() => setClassOptions([]));
  }, [schoolSlug, role]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      const { photograph, ...payload } = form;
      if (!payload.password) delete payload.password;
      let result = mode === "edit" ? await schoolApi.updateEmployee(schoolSlug, role, selected.id, payload) : await schoolApi.createEmployee(schoolSlug, role, payload);
      if (uploads.profile) result = await schoolApi.uploadEmployeePhoto(schoolSlug, role, result.id, uploads.profile);
      if (uploads.face) result = await schoolApi.uploadEmployeeDocument(schoolSlug, role, result.id, uploads.face, "Face Attendance Photo");
      if (uploads.document) result = await schoolApi.uploadEmployeeDocument(schoolSlug, role, result.id, uploads.document, "Employee Document");
      setSelected(result);
      setUploads({ profile: null, face: null, document: null });
      setMode("");
      setNotice(`${title.slice(0, -1)} details and uploads saved successfully.`);
      await load();
    } catch (e) {
      setNotice(e.message || "Unable to save the employee. Check the highlighted details and try again.");
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!window.confirm(`Delete ${selected.name}?`)) return;
    try {
      await schoolApi.deleteEmployee(schoolSlug, role, selected.id);
      setSelected(null);
      setNotice("Employee removed.");
      load();
    } catch (e) {
      setNotice(e.message);
    }
  };
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    try {
      const r = await schoolApi.bulkUploadEmployees(schoolSlug, role, file);
      setNotice(`${r.created} ${title.toLowerCase()} imported.${r.errors.length ? ` ${r.errors.length} row(s) need attention.` : ""}`);
      load();
    } catch (err) {
      setNotice(err.message);
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const fields = [
    ["name", "Full name *"],
    ["email", "Email *", "email"],
    ["password", "Temporary password *", "password"],
    ["employee_code", "Employee code *"],
    ["designation", "Designation"],
    ["department", "Department"],
    ["phone", "Phone"],
    ["joining_date", "Joining date", "date"],
    ["salary", "Monthly salary", "number"],
    ["qualification", "Qualification"],
    ["experience", "Experience"],
    ["subjects_assigned", "Subjects assigned"],
    ["class_teacher_of", "Class teacher of"],
    ["address", "Address"],
  ];
  const shown = items.filter((x) => `${x.name} ${x.employee_code} ${x.department} ${x.designation}`.toLowerCase().includes(search.toLowerCase()));
  return (
    <EmployeeDirectoryUI
      {...{
        title,
        role,
        schoolSlug,
        backRoute,
        navigate,
        items,
        selected,
        setSelected,
        form,
        setForm,
        mode,
        setMode,
        search,
        setSearch,
        notice,
        setNotice,
        busy,
        classOptions,
        uploads,
        setUploads,
        fields,
        save,
        remove,
        upload,
        load,
      }}
    />
  );
  // Legacy inline markup below is intentionally unreachable during the
  // transition and can be removed after downstream visual snapshots update.
  return (
    <main className="min-h-screen bg-[#0A1128] p-6 text-slate-100 sm:p-10">
      <div className="mx-auto max-w-7xl">
        <button onClick={() => navigate(backRoute, schoolSlug)} className="mb-3 text-xs text-amber-400">
          ← Back to dashboard
        </button>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:justify-between">
          <div>
            <h1 className="text-3xl font-black">{title}</h1>
            <p className="text-sm text-slate-400">Profiles, work information and login credentials.</p>
          </div>
          <div className="flex gap-3">
            <label className="cursor-pointer rounded-xl border border-slate-700 bg-slate-900 px-4 py-3 text-sm font-bold">
              Import Excel / CSV
              <input type="file" className="hidden" accept=".xlsx,.csv" onChange={upload} />
            </label>
            <button
              onClick={() => {
                setForm(blank);
                setMode("add");
              }}
              className="rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-slate-950"
            >
              + Add {title.slice(0, -1)}
            </button>
          </div>
        </div>
        {notice && <p className="mb-4 rounded-xl bg-indigo-950 p-3 text-sm text-indigo-200">{notice}</p>}
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`} className="mb-5 w-full rounded-xl border border-slate-700 bg-slate-900 p-3" />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((item) => (
            <button key={item.id} onClick={() => setSelected(item)} className="rounded-2xl border border-slate-800 bg-slate-900 p-5 text-left hover:border-indigo-500">
              <div className="flex gap-3">
                <img src={item.photograph || `https://ui-avatars.com/api/?background=4f46e5&color=fff&name=${encodeURIComponent(item.name)}`} alt="" className="h-14 w-14 rounded-xl object-cover" />
                <div>
                  <h2 className="font-bold">{item.name}</h2>
                  <p className="text-xs text-amber-400">{item.designation || role}</p>
                  <p className="text-xs text-slate-400">
                    {item.department || "—"} · {item.employee_code}
                  </p>
                </div>
              </div>
              <p className="mt-4 text-xs text-slate-400">
                {item.email} · {item.phone || "No phone"}
              </p>
            </button>
          ))}
          {!shown.length && <p className="text-slate-500">No {title.toLowerCase()} found.</p>}
        </div>
      </div>
      {selected && !mode && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 p-4">
          <div className="mx-auto my-8 max-w-2xl rounded-3xl bg-slate-900 p-6">
            <div className="flex justify-between">
              <div className="flex gap-4">
                <img src={selected.photograph || `https://ui-avatars.com/api/?background=4f46e5&color=fff&name=${encodeURIComponent(selected.name)}`} alt="" className="h-20 w-20 rounded-2xl" />
                <div>
                  <h2 className="text-2xl font-black">{selected.name}</h2>
                  <p className="text-amber-400">
                    {selected.designation} · {selected.employee_code}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {[
                ["Email", selected.email],
                ["Phone", selected.phone],
                ["Department", selected.department],
                ["Joining date", selected.joining_date],
                ["Salary", selected.salary && `₹${Number(selected.salary).toLocaleString()}`],
                ["Qualification", selected.qualification],
                ["Experience", selected.experience],
                ["Subjects", selected.subjects_assigned],
                ["Class teacher", selected.class_teacher_of],
                ["Address", selected.address],
              ].map(([l, v]) => (
                <div key={l} className="rounded-xl bg-slate-950 p-3">
                  <small className="text-slate-500">{l}</small>
                  <p className="text-sm font-semibold">{v || "—"}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => {
                  setForm({ ...selected, password: "" });
                  setMode("edit");
                }}
                className="rounded-xl bg-amber-500 px-4 py-3 font-bold text-slate-950"
              >
                Edit
              </button>
              <button onClick={remove} className="rounded-xl bg-red-600 px-4 py-3 font-bold">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {mode && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/85 p-4">
          <form onSubmit={save} className="mx-auto my-6 max-w-3xl rounded-3xl bg-slate-900 p-6">
            <div className="mb-5 flex justify-between">
              <h2 className="text-xl font-black">{mode === "edit" ? `Edit ${title.slice(0, -1)}` : `Add ${title.slice(0, -1)}`}</h2>
              <button type="button" onClick={() => setMode("")}>
                ✕
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map(([name, label, type]) => (
                <label key={name} className="text-xs font-semibold text-slate-300">
                  {label}
                  <input name={name} type={type || "text"} required={mode === "add" && ["name", "email", "password", "employee_code"].includes(name)} value={form[name] || ""} onChange={(e) => setForm({ ...form, [name]: e.target.value })} className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm" />
                </label>
              ))}
            </div>
            <button disabled={busy} className="mt-6 w-full rounded-xl bg-amber-500 py-3 font-bold text-slate-950">
              {busy ? "Saving…" : "Save profile"}
            </button>
          </form>
        </div>
      )}
    </main>
  );
}
export const TeachersModule = (p) => <EmployeeModule {...p} role="teacher" />;
export const StaffModule = (p) => <EmployeeModule {...p} role="staff" />;
export const AccountantsModule = (p) => <EmployeeModule {...p} role="accountant" />;

export function ExamsModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Dashboard
        </button>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl sm:text-3xl font-black text-white">Exam & Result Management</h1>
          <button onClick={() => alert("Exam created successfully.")} className="bg-amber-500 text-slate-950 font-bold px-4 py-2.5 rounded-xl text-xs" data-testid="create-exam-btn">
            + Create Exam
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {(isBlankWorkspace ? [] : examsData).map((ex) => (
            <div key={ex.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
              <h3 className="font-bold text-white text-lg">{ex.examName}</h3>
              <div className="text-xs text-amber-400 mt-1">
                {ex.class} • Session: {ex.academicSession}
              </div>
              <div className="text-xs text-slate-400 mt-2">
                Dates: {ex.startDate} to {ex.endDate}
              </div>
              <div className="mt-4 flex gap-3">
                <button onClick={() => alert("Marks entry portal opened.")} className="bg-indigo-600 text-white px-4 py-2 rounded-xl text-xs font-bold" data-testid={`enter-marks-${ex.id}`}>
                  Enter Marks
                </button>
                <button onClick={() => alert("Printable marksheet generated.")} className="bg-slate-800 text-white px-4 py-2 rounded-xl text-xs font-bold" data-testid={`print-marksheet-${ex.id}`}>
                  Print Marksheet
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function TimetableModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-6">Weekly Timetable Grid (Class 10-A)</h1>
        <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950 text-slate-400 text-xs uppercase">
              <tr>
                <th className="p-4">Day</th>
                <th className="p-4">Period & Timing</th>
                <th className="p-4">Subject</th>
                <th className="p-4">Teacher</th>
                <th className="p-4">Room</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300">
              {(isBlankWorkspace ? [] : timetableData).map((tb, idx) => (
                <tr key={idx}>
                  <td className="p-4 font-bold text-white">{tb.day}</td>
                  <td className="p-4 text-xs text-amber-400">{tb.period}</td>
                  <td className="p-4 font-semibold">{tb.subject}</td>
                  <td className="p-4">{tb.teacher}</td>
                  <td className="p-4 text-xs">{tb.room}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export function HomeworkNoticesModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-6">Homework & Daily Assignments</h1>
        <div className="space-y-4">
          {(isBlankWorkspace ? [] : homeworkData).map((hw) => (
            <div key={hw.id} className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-bold text-white text-base">{hw.title}</h3>
                <span className="text-xs bg-indigo-950 text-indigo-300 px-3 py-1 rounded-full">
                  {hw.class} • {hw.subject}
                </span>
              </div>
              <p className="text-xs text-slate-300">{hw.description}</p>
              <div className="mt-4 text-[11px] text-amber-400">Due Date: {hw.dueDate}</div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

export function AdmissionCRMModule({ schoolSlug, navigate, isBlankWorkspace = false }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Dashboard
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-6">Admission Enquiry CRM</h1>
        <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-slate-800 bg-slate-950 text-slate-400 text-xs uppercase">
              <tr>
                <th className="p-4">Student & Parent</th>
                <th className="p-4">Requested Class</th>
                <th className="p-4">Phone / WhatsApp</th>
                <th className="p-4">Status</th>
                <th className="p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 text-slate-300">
              {(isBlankWorkspace ? [] : admissionEnquiriesData).map((enq) => (
                <tr key={enq.id}>
                  <td className="p-4">
                    <div className="font-bold text-white">{enq.studentName}</div>
                    <div className="text-xs text-slate-400">Parent: {enq.parentName}</div>
                  </td>
                  <td className="p-4 font-semibold text-amber-400">{enq.requestedClass}</td>
                  <td className="p-4 text-xs">📞 {enq.phone}</td>
                  <td className="p-4">
                    <span className="px-2.5 py-1 rounded-full text-xs bg-emerald-950 text-emerald-400">{enq.status}</span>
                  </td>
                  <td className="p-4">
                    <a href={`https://wa.me/${enq.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-xs bg-[#25D366] text-white px-3 py-1.5 rounded-lg font-bold" data-testid={`whatsapp-enq-${enq.id}`}>
                      WhatsApp Chat
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

export function StudentDashboard({ schoolSlug, navigate }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Workspace
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">Student Portal</h1>
        <p className="text-slate-400 text-sm mb-8">Isolated view for student self-service portal.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Attendance Percentage</div>
            <div className="text-3xl font-black text-emerald-400 mt-2">—</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Fee Status</div>
            <div className="text-3xl font-black text-amber-400 mt-2">No records</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Pending Homework</div>
            <div className="text-3xl font-black text-indigo-400 mt-2">0 Tasks</div>
          </div>
        </div>
      </main>
    </div>
  );
}

export function ParentDashboard({ schoolSlug, navigate }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Workspace
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">Parent Portal</h1>
        <p className="text-slate-400 text-sm mb-8">Your linked student information will appear here.</p>
        <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
          <h3 className="font-bold text-white text-lg mb-4">Child Performance & Fee Summary</h3>
          <p className="text-xs text-slate-300">All homework, report cards, and fee payment receipts are synced in real-time.</p>
        </div>
      </main>
    </div>
  );
}

export function TeacherDashboard({ schoolSlug, navigate }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Workspace
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">Teacher Dashboard</h1>
        <p className="text-slate-400 text-sm mb-8">Assigned classes, attendance, homework, and student activity.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Assigned Students</div>
            <div className="text-3xl font-black text-white mt-2">0</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Today's Classes</div>
            <div className="text-3xl font-black text-amber-400 mt-2">0 Periods</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Pending Homework Review</div>
            <div className="text-3xl font-black text-indigo-400 mt-2">0 Submissions</div>
          </div>
        </div>
      </main>
    </div>
  );
}

export function AccountantDashboard({ schoolSlug, navigate }) {
  return (
    <div className="min-h-screen bg-[#0A1128] text-slate-100 p-4 sm:p-8">
      <main className="max-w-7xl mx-auto flex-1">
        <button onClick={() => navigate("school-admin", schoolSlug)} className="text-xs text-amber-400 mb-4 block">
          ← Back to Workspace
        </button>
        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">Accountant Portal</h1>
        <p className="text-slate-400 text-sm mb-8">Fee collections, Razorpay gateway receipts, and financial reports.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Collected Today</div>
            <div className="text-3xl font-black text-emerald-400 mt-2">₹0</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Monthly Collection</div>
            <div className="text-3xl font-black text-amber-400 mt-2">₹0</div>
          </div>
          <div className="bg-slate-900 border border-slate-800 p-6 rounded-3xl">
            <div className="text-slate-400 text-xs">Pending Defaulters</div>
            <div className="text-3xl font-black text-rose-400 mt-2">0 Students</div>
          </div>
        </div>
      </main>
    </div>
  );
}
